import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  catchError,
  filter,
  forkJoin,
  map,
  mergeMap,
  of,
  pipe,
  tap,
} from 'rxjs';
import {
  IWHWorkerDTO,
  WHDataService,
  WHWorkerDOM,
} from '@workheld/workheld-shared-lib';
import { DataModelHelperService } from '../app-services-helper/data-model-helper.service';

export interface WorkerCacheEntry {
  name: string;
  avatarUrl: string;
  extId: string;
  workerDOM: WHWorkerDOM | null;
}

interface WorkerLookupState {
  cache: Record<string, WorkerCacheEntry>;
  loading: Record<string, boolean>;
}

const initialState: WorkerLookupState = {
  cache: {},
  loading: {},
};

function createEmptyCacheEntry(): WorkerCacheEntry {
  return { name: '', avatarUrl: '', extId: '', workerDOM: null };
}

function buildCacheEntry(
  dto: IWHWorkerDTO,
  dataModelHelper: DataModelHelperService,
): WorkerCacheEntry {
  const dtoAny = dto as unknown as Record<string, unknown>;
  const workerDOM = dataModelHelper.initWorkerDOM(dto);
  const lastname = (dtoAny['lastname'] as string) || '';
  const firstname = (dtoAny['firstname'] as string) || '';
  const name = lastname
    ? `${lastname} ${firstname}`
    : (dtoAny['name'] as string) || '';
  const avatarUrl = (dtoAny['avatarUrl'] as string) || '';
  const extId = (dtoAny['extId'] as string) || '';

  return { name, avatarUrl, extId, workerDOM };
}

export const WorkerLookupStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed(({ cache }) => ({
    workerByUrl: computed(() => cache()),
  })),

  withMethods(
    (
      store,
      dataService = inject(WHDataService),
      dataModelHelper = inject(DataModelHelperService),
    ) => ({
      getWorker(url: string): WorkerCacheEntry | undefined {
        return store.cache()[url];
      },

      loadWorker: rxMethod<string>(
        pipe(
          filter(
            (url) => !!url && !store.cache()[url] && !store.loading()[url],
          ),
          tap((url) =>
            patchState(store, (state) => ({
              loading: { ...state.loading, [url]: true },
            })),
          ),
          mergeMap((url) =>
            dataService.getByUrl(url).pipe(
              map((dto) => ({ url, dto })),
              catchError(() => of({ url, dto: null as IWHWorkerDTO | null })),
            ),
          ),
          tapResponse({
            next: ({ url, dto }) => {
              const entry = dto
                ? buildCacheEntry(dto, dataModelHelper)
                : createEmptyCacheEntry();
              patchState(store, (state) => ({
                cache: { ...state.cache, [url]: entry },
                loading: { ...state.loading, [url]: false },
              }));
            },
            error: () => {},
          }),
        ),
      ),

      loadWorkers: rxMethod<string[]>(
        pipe(
          map((urls) => [
            ...new Set(
              urls.filter(
                (url) => url && !store.cache()[url] && !store.loading()[url],
              ),
            ),
          ]),
          filter((urls) => urls.length > 0),
          tap((urls) => {
            const loadingEntries = Object.fromEntries(
              urls.map((url) => [url, true]),
            );
            patchState(store, (state) => ({
              loading: { ...state.loading, ...loadingEntries },
            }));
          }),
          mergeMap((urls) =>
            forkJoin(
              urls.map((url) =>
                dataService.getByUrl(url).pipe(
                  map((dto) => ({ url, dto })),
                  catchError(() =>
                    of({ url, dto: null as IWHWorkerDTO | null }),
                  ),
                ),
              ),
            ),
          ),
          tapResponse({
            next: (results: { url: string; dto: IWHWorkerDTO | null }[]) => {
              const newCache: Record<string, WorkerCacheEntry> = {};
              const newLoading: Record<string, boolean> = {};
              for (const { url, dto } of results) {
                newCache[url] = dto
                  ? buildCacheEntry(dto, dataModelHelper)
                  : createEmptyCacheEntry();
                newLoading[url] = false;
              }
              patchState(store, (state) => ({
                cache: { ...state.cache, ...newCache },
                loading: { ...state.loading, ...newLoading },
              }));
            },
            error: () => {},
          }),
        ),
      ),
    }),
  ),
);
